import { useRef, useState } from "react";
import { Camera, Upload, FileCheck2, X, Loader2, Eye } from "lucide-react";
import { CATALOGO_DOCS } from "../constants";
import CameraCaptura, { cameraNoSiteDisponivel } from "./CameraCaptura";

// Todo tipo que a ficha sabe nomear — pedido, emitido por nós ou herdado de
// fichas antigas. Serve para saber o que é anexo realmente solto.
const CATALOGO_COMPLETO = CATALOGO_DOCS;

// Campos de documento rotulados por tipo (Cartão CNPJ, Contrato Social, etc.).
// Cada arquivo enviado carrega o `tipo` certo — é o que faz a manutenção/
// combustível contarem como documento obrigatório, sem o fornecedor reenviar.
//
// Presentational: o upload e o estado ficam no formulário-pai (que já tem o
// Storage e o id da ficha). Aqui só mostramos os campos e disparamos onPick.

function Slot({ t, arquivos, onPick, onRemove, onView, isMobile, processing, disabled, obrigatorio }) {
  const camRef = useRef(null);
  const fileRef = useRef(null);
  const [camera, setCamera] = useState(false);
  // Câmera na própria página: o app de câmera do Android fazia o navegador ser
  // fechado, e o fornecedor voltava para uma ficha recarregada e vazia.
  const cameraNoSite = cameraNoSiteDisponivel();
  const pick = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length) onPick(t.tipo, files);
  };
  return (
    <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: "10px 12px", background: arquivos.length ? "#f0fdfa" : "#fff" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
        {/* Sem "(opcional)": o que não é obrigatório aqui é pedido depois, não
            é dispensável — dizer "opcional" fazia o fornecedor pular e a equipe
            correr atrás do mesmo papel semanas depois. */}
        <div style={{ fontSize: 12.5, fontWeight: 700, color: "#334155" }}>
          {t.label} {obrigatorio && <span style={{ color: "#dc2626" }}>*</span>}
        </div>
      </div>
      {t.dica && <div style={{ fontSize: 10.5, color: "#94a3b8", marginTop: 1 }}>{t.dica}</div>}

      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
        {isMobile && (
          <button type="button" onClick={() => (cameraNoSite ? setCamera(true) : camRef.current?.click())} disabled={processing || disabled} style={btn}>
            <Camera size={14} /> Tirar foto
          </button>
        )}
        <button type="button" onClick={() => fileRef.current?.click()} disabled={processing || disabled} style={btn}>
          <Upload size={14} /> {isMobile ? "Da galeria" : "Anexar"}
        </button>
        <input ref={camRef} type="file" accept="image/*" capture="environment" onChange={pick} style={{ display: "none" }} />
        <input ref={fileRef} type="file" accept="image/*,application/pdf" multiple onChange={pick} style={{ display: "none" }} />
        <CameraCaptura aberta={camera} onFechar={() => setCamera(false)} onFoto={(f) => onPick(t.tipo, [f])} lado={2400} prefixo="documento" />
      </div>

      {arquivos.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
          {arquivos.map((d) => (
            <div key={d.path} style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 9px", borderRadius: 8, background: "#fff", border: "1px solid #99f6e4", fontSize: 11.5 }}>
              <FileCheck2 size={13} color="#0d9488" />
              <span style={{ maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.nome}</span>
              {onView && <button type="button" onClick={() => onView(d.path)} title="Ver" style={{ background: "none", border: "none", cursor: "pointer", color: "#0d9488", display: "flex" }}><Eye size={13} /></button>}
              {!disabled && <button type="button" onClick={() => onRemove(d.path)} style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", display: "flex" }}><X size={13} /></button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function DocsCredenciamento({ tipos, docs, onPick, onRemove, onView, isMobile, processing, disabled, obrigatorios = [], outros = true }) {
  // "Outros anexos" é o que não tem campo em NENHUMA etapa — não o que ficou de
  // fora DESTE bloco. Comparar só com os `tipos` daqui fazia o cartão CNPJ da 2ª
  // etapa aparecer como anexo solto no bloco da assinatura, duplicado.
  const conhecidos = new Set([...CATALOGO_COMPLETO.map((t) => t.tipo), ...tipos.map((t) => t.tipo)]);
  const legados = outros ? (docs || []).filter((d) => !conhecidos.has(d.tipo)) : [];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {tipos.map((t) => (
        <Slot
          key={t.tipo}
          t={t}
          arquivos={(docs || []).filter((d) => d.tipo === t.tipo)}
          onPick={onPick}
          onRemove={onRemove}
          onView={onView}
          isMobile={isMobile}
          processing={processing}
          disabled={disabled}
          obrigatorio={obrigatorios.includes(t.tipo)}
        />
      ))}

      {processing && (
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, color: "#64748b" }}>
          <Loader2 size={13} className="spin" /> Enviando...
        </div>
      )}

      {legados.length > 0 && (
        <div style={{ marginTop: 2 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", marginBottom: 4 }}>Outros anexos</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {legados.map((d) => (
              <div key={d.path} style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 9px", borderRadius: 8, background: "#f8fafc", border: "1px solid #e2e8f0", fontSize: 11.5 }}>
                <FileCheck2 size={13} color="#64748b" />
                <span style={{ maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.nome}</span>
                {onView && <button type="button" onClick={() => onView(d.path)} title="Ver" style={{ background: "none", border: "none", cursor: "pointer", color: "#0d9488", display: "flex" }}><Eye size={13} /></button>}
                {!disabled && <button type="button" onClick={() => onRemove(d.path)} style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", display: "flex" }}><X size={13} /></button>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const btn = { display: "flex", alignItems: "center", gap: 5, padding: "8px 12px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontSize: 12, fontWeight: 600, cursor: "pointer" };
